<template>
  <div v-if="!categoryIndex">
    <ChooseCategory @categorySelection="getCategoryDetails" />
  </div>
  <div v-else>
    <NavBar
      class="w-full fixed mb-10"
      :category-name="selectedCategoryName"
      :all-orders-count="selectedCategoryOrdersCount"
      :in-preparation-orders-count="selectedCategoryOrders.length"
      @refreshClick="
        () => {
          fetchCategoryOrders();
        }
      "
      @openMenuClick="displaySidebarMenu = true"
      @newOrderClick="displayNewOrderModal = true"
    />

    <div class="w-full inline-block mt-10 pt-8">
      <div class="grid gap-4 aggregateGrid">
        <AggregatedOrders
          v-if="selectedCategoryAggregatedOrders.length"
          class="h-full col-start-1 col-end-2 row-start-1 row-end-3 aggregated-wrraper"
          :orders="selectedCategoryAggregatedOrders"
        />
        <OngoingOrders
          v-if="selectedCategoryOrders.length"
          ref="ordersComponent"
          class="row-start-1 col-start-2 col-end-3 row-end-2"
          :orders="selectedCategoryOrders"
          :current-order-index="selectedOrderIndex"
          @orderSelection="displayOrderDetails"
        />
        <EmptyOrders
          v-if="!selectedCategoryOrders.length"
          :has-orders="selectedCategoryOrdersCount"
          class="mx-auto col-start-2 col-end-3"
          @newOrderClick="displayNewOrderModal = true"
        />
        <!-- 
        <UpcomingOrders
          class="col-start-2 col-end-3"
          :class="{
            'row-start-3': !selectedCategoryOrders.length,
            'row-start-2': selectedCategoryOrders.length,
          }"
          :orders="selectedCategoryUpcomingOrders"
        /> -->
        <OrderDetails
          v-if="displayDetails"
          ref="detailsComponent"
          class="order-details-wrraper w-full col-start-3 col-end-4 justify-self-end"
          :order="selectedOrder"
          :tolerance="tolerance"
          @newOrderStatus="handleNewItemStatus"
          @finishOrderClick="finishOrder"
        />
      </div>
      <DissmissalModal
        v-if="openDismissalModal"
        :reasons="dismissReasonsList"
        :options="alternativeProductsList"
        @close="openDismissalModal = false"
        @submit="handleDismissalSubmit"
      />
    </div>

    <SideBarMenu
      v-show="displaySidebarMenu"
      class="fixed top-0"
      @categorySelection="getCategoryDetails"
      @logoutClick="removeAuthentication"
      @click.stop="displaySidebarMenu = true"
      @closeMenuClick="displaySidebarMenu = false"
    />

    <StartOrderModal
      v-if="displayNewOrderModal"
      class="w-full"
      :valid-code="isValidCode"
      :can-start="canPull"
      @closeModalClick="displayNewOrderModal = false"
      @newOrderCode="startNewOrder"
    />

    <Alert
      v-if="displayAlertModal"
      class="fixed top-0"
      :content="alertMessage"
      @closeAlertClick="displayAlertModal = false"
    />
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, onBeforeUnmount, shallowRef } from 'vue';
import ChooseCategory from '@/components/ChooseCategory';
import NavBar from '@/components/CategoriesNavBar';
import OngoingOrders from '@/components/OngoingOrders';
import EmptyOrders from '@/components/EmptyOrders';
import OrderDetails from '@/components/OrderDetails';
import SideBarMenu from '@/components/SideBarMenu';
import StartOrderModal from '@/components/StartOrderModal';
import DissmissalModal from '@/components/DissmissalModal';
import Alert from '@/components/Alert';
import UpcomingOrders from '@/components/UpcomingOrders';
import AggregatedOrders from '@/components/AggregatedOrders';
import firebaseMessaging from './firebase'; // Uncomment if you are using firebase messaging
import { useOrderNotifications } from '@/composables/notifications';

import axios from 'axios';

// --- State Variables ---
const selectedCategoryOrders = ref([]);
const selectedOrder = ref({});
const categoryIndex = ref(0);
const selectedCategoryName = ref('');
const selectedCategoryValue = ref('');
const selectedCategoryOrdersCount = ref(0);
const inPreparationOrdersCount = ref(0); // This was in data but not used, kept for completeness
const selectedOrderIndex = ref(0);
const rejectedItem = ref(null);
const displaySidebarMenu = ref(false);
const displayNewOrderModal = ref(false);
const displayAlertModal = ref(false);
const displayDetails = ref(false);
const finishedOrderTime = ref('');
const canPull = ref(true);
const isValidCode = ref(true);
const tolerance = ref(0);
const selectedCategoryUpcomingOrders = ref([]);
const selectedCategoryAggregatedOrders = ref([]);
const openDismissalModal = ref(false);
const dismissReasonsList = ref([]); // This was in data but not used, kept for completeness
const alternativeProductsList = ref([]); // This was in data but not used, kept for completeness
const alertMessage = ref(''); // This was in data but not used, kept for completeness
const cachedOrders = ref([]); // This was in data but not used, kept for detecting new orders
const cachedSlaOrders = ref([]); // This was in data but not used, kept for not repeating notifications
const categories = reactive([
  { name: 'قسم اللحوم', value: 'MEAT' },
  { name: 'قسم الجبن', value: 'CHEESE' },
  { name: 'قسم الأسماك', value: 'FISH' },
  { name: 'قسم الدواجن', value: 'CHICKEN' },
]);

// --- Template Refs ---
const ordersComponent = shallowRef(null);
const detailsComponent = shallowRef(null);

const { notify, notifyAlarm } = useOrderNotifications();
// --- Emits ---
const emit = defineEmits(['setAuthentication', 'removeAuthentication']);

// --- Axios Instance ---
let instance = null;
const timezone = 'Africa/Cairo';

// --- Methods ---
const configureAxiosInstance = () => {
  instance = axios.create({
    baseURL: process.env.VUE_APP_API_ENDPOINT, // Use VITE_APP_ for Vite or process.env for Webpack
    headers: {
      'Content-Type': 'application/json',
      Authorization: `${
        document.cookie
          .split('; ')
          .filter(row => row.startsWith('access'))[0]
          .split('=')[1]
      }`,
    },
  });
};

const getNotificationToken = () => {
  firebaseMessaging
    .getToken({
      vapidKey: process.env.VUE_APP_VAPID_KEY, // Use VITE_APP_ for Vite or process.env for Webpack
    })
    .then(token => {
      sessionStorage.setItem('notification_token', token);
      sendNotificationToken(token);
    });
};

const sendNotificationToken = token => {
  let tokenFormData = new FormData();
  tokenFormData.append('token', token);
  instance({
    method: 'post',
    url: `/api/notifications/kitchen/${selectedCategoryValue.value}/subscribe`,
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    data: tokenFormData,
  }).then(() => {
    subscribeForEvents();
  });
};

const subscribeForEvents = () => {
  firebaseMessaging.onMessage(msg => {
    fetchCategoryOrders();
  });

  firebaseMessaging.onBackgroundMessage(msg => {
    fetchCategoryOrders();
    self.registration.showNotification();
  });
};

const displayOrderDetails = e => {
  displayDetails.value = false;
  setTimeout(() => {
    displayDetails.value = true;
    selectedOrderIndex.value = e.orderIndex;
    selectedOrder.value = e.order;
  }, 1);
};

const getDismissalData = () => {
  instance
    .get(`/api/dismissal-reasons/kitchen`)
    .then(response => {
      dismissReasonsList.value = response.data;
    })
    .catch(() => {
      refreshAccessToken();
    });
};

const getAltProducts = id => {
  instance
    .get(`/api/item/${id}/alt-products`)
    .then(response => {
      alternativeProductsList.value = response.data.data;
    })
    .catch(() => {
      refreshAccessToken();
    });
};
const handleDismissalSubmit = data => {
  // update order with alt products
  const selectedOrderItem = selectedOrder.value.items.find(item => item.id === rejectedItem.value);

  // Here you would typically make an API call to submit the dismissal
  instance
    .post(`/api/items/${rejectedItem.value}/kitchen/V2/reject`, {
      reason_id: data.reason,
      comment: data.comment,
    })
    .then(response => {
      // update the rejected reason
      selectedOrderItem.dismissal_reason = response?.data?.data?.dismissal_reason ?? null;
    });

  instance
    .post(`/api/item/${rejectedItem.value}/alt-products/kitchen`, {
      code: `${data.alternatives.join(',')}`,
    })
    .then(response => {
      // get the alt products
      selectedOrderItem.alt_products_data = response?.data?.data?.alt_products_details ?? [];
    });

  if (data?.altProductCode) {
    instance
      .post(`/api/item/${rejectedItem.value}/alt-product-proxy`, {
        sku: data.altProductCode,
      })
      .then(response => {
        selectedOrderItem.alt_products_data = response?.data?.data ?? [];
      });
  }

  alertMessage.value = `
     تم رفض المنتج بنجاح
      <br><br>
    سبب الرفض: ${data.reasonName}
    <br><br>
    البدائل المختارة: 
    ${data.optionsNames.map(option => `<br>- ${option}`).join('')}
    ${data.comment ? `<br><br>ملاحظات: ${data.comment}` : ''}
      <br><br>
      كود المنتج البديل: ${data.altProductCode || 'لا يوجد'}
  `;
  displayAlertModal.value = true;
  openDismissalModal.value = false;
  getCategoryOrders();
  getCategoryAggregatedOrders();
  setTimeout(() => {
    displayAlertModal.value = false;
    alertMessage.value = '';
  }, 5000);
  fetchCategoryOrders();
};

const handleNewItemStatus = e => {
  if (e.itemStatus !== 'IN_PROGRESS') {
    getDismissalData();
    getAltProducts(e.itemID);
    rejectedItem.value = e.itemID;
    openDismissalModal.value = true;
    return;
  }

  instance
    .post(`/api/items/${e.itemID}/kitchen/verify`, {
      qty: e.itemQuantity,
    })
    .then(() => {
      selectedOrder.value.items[e.itemNumber].is_prepared = 1;
      selectedOrder.value.items[e.itemNumber].status = e.itemStatus;
      // Accessing child component methods via template refs
      if (detailsComponent.value && typeof detailsComponent.value.updateQuantity === 'function') {
        detailsComponent.value.updateQuantity(e.itemNumber);
      }
    });
};

const finishOrder = data => {
  instance.post(`/api/orders/order/${data.id}/${selectedCategoryValue.value}/finish`).then(() => {
    finishedOrderTime.value = selectedOrder.value.time;
    alertMessage.value = `تم انهاء تنفيذ الطلب بنجاح! الوقت المستغرق ${selectedOrder.value.time} دقيقة`;
    displayAlertModal.value = true;
    selectedCategoryOrders.value.splice(selectedOrderIndex.value, 1);
    selectedOrder.value = null;
    displayDetails.value = false;

    setTimeout(() => {
      displayAlertModal.value = false;
    }, 5000);
  });
};

const startNewOrder = data => {
  let formData = new FormData();
  formData.append('code', data.code);

  instance({
    method: 'post',
    url: `/api/orders/${selectedCategoryValue.value}/pull`,
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    data: formData,
  })
    .then(response => {
      let order = response.data.order;
      selectedCategoryOrders.value.push(order);
      configureOrderTimeRelatedData();
      displayNewOrderModal.value = false;
      if (selectedCategoryOrdersCount.value) selectedCategoryOrdersCount.value -= 1;
      selectedOrderIndex.value = selectedCategoryOrders.value.length - 1;
      selectedOrder.value = selectedCategoryOrders.value[selectedOrderIndex.value];
      displayDetails.value = true;
      ordersComponent.value.selectThePulledOrder(selectedOrderIndex.value);
    })
    .catch(error => {
      if (error?.response?.status === 500) canPull.value = false;
      else if (error?.response?.status === 400) isValidCode.value = false;

      setTimeout(() => {
        displayNewOrderModal.value = false;
        canPull.value = true;
        isValidCode.value = true;
      }, 5000);
    });
  getCategoryAggregatedOrders();
};

const getCategoryDetails = e => {
  selectedOrder.value = null;
  displayDetails.value = false;
  displaySidebarMenu.value = false;

  configureCategoryInfo(e);
};

const configureCategoryInfo = e => {
  if (e?.categoryIndex !== undefined) {
    sessionStorage.setItem('categoryIndex', e.categoryIndex);
    categoryIndex.value = e.categoryIndex;
  } else {
    categoryIndex.value = sessionStorage.getItem('categoryIndex') || 0;
  }

  if (categoryIndex.value) {
    selectedCategoryValue.value = categories[categoryIndex.value - 1].value;
    selectedCategoryName.value = categories[categoryIndex.value - 1].name;
    fetchCategoryOrders();
    getNotificationToken();

    // unsubscribe from previous pooling
    unsubscribeFromPooling();
    // subscribe for new pooling
    subscribeForPooling();
  }
};

const getCategoryOrders = () => {
  instance
    .get(`/api/orders/${selectedCategoryValue.value}/details`)
    .then(response => {
      selectedCategoryOrders.value = response.data.orders;
      selectedCategoryOrdersCount.value = response.data.count;

      if (selectedCategoryOrdersCount.value) {
        notify(selectedCategoryOrdersCount.value);
      }

      configureOrderTimeRelatedData();
      getTolerance();
    })
    .catch(error => {
      console.log(error);
      refreshAccessToken();
    });
};

const getCategoryUpcomingOrders = () => {
  instance
    .get(`/api/orders/${selectedCategoryValue.value}/view-upcoming`)
    .then(response => {
      selectedCategoryUpcomingOrders.value = response?.data?.orders || [];
    })
    .catch(() => {
      refreshAccessToken();
    });
};

const getCategoryAggregatedOrders = () => {
  instance
    .get(`/api/orders/${selectedCategoryValue.value}/aggregated`)
    .then(response => {
      selectedCategoryAggregatedOrders.value = response?.data?.data || [];
    })
    .catch(() => {
      refreshAccessToken();
    });
};

const getTolerance = () => {
  instance.get(`/api/items/tolerance`).then(response => {
    tolerance.value = response.data.tolerance_rate / 100;
  });
};

const refreshAccessToken = () => {
  return instance
    .post('/api/oauth/token', {
      grant_type: 'refresh_token',
      client_id: process.env.VUE_APP_CLIENT_ID, // Use VITE_APP_ for Vite or process.env for Webpack
      client_secret: process.env.VUE_APP_CLIENT_SECRET, // Use VITE_APP_ for Vite or process.env for Webpack
      refresh_token: localStorage.getItem('refreshToken'),
      scope: '',
    })
    .then(response => {
      emit('setAuthentication', response.data);
      configureAxiosInstance();
      fetchCategoryOrders();
    })
    .catch(() => {
      removeAuthentication();
    });
};

const clearIntervals = () => {
  // Clear all intervals to prevent memory leaks
  intervals.value.forEach(interval => clearInterval(interval));
  intervals.value = [];
};

const intervals = ref([]);
const configureOrderTimeRelatedData = () => {
  if (!selectedCategoryOrders.value?.length) {
    clearIntervals();
    return;
  }

  selectedCategoryOrders.value.forEach(order => {
    // Clear any existing intervals to prevent multiple intervals for the same order
    // (This requires storing the interval IDs, which is not currently done,
    // but good practice for robustness in a real application)

    // Clear previous intervals for this order
    clearIntervals();

    // For simplicity, re-establishing a new interval for each order on data refresh
    // might lead to multiple intervals if not managed carefully.
    // In a real app, consider using `watch` on selectedCategoryOrders and clearing
    // intervals on old orders before setting new ones.
    intervals.value.push(
      setInterval(() => {
        calculateTiming(order);
      }, 1000)
    );
  });
};

const calculateTiming = o => {
  const startTime = new Date(o.kitchen_started_at).getTime();
  const currentTime = new Date().getTime();
  if (currentTime < startTime) return;
  const now = currentTime;

  o.slaTimeHours = new Date(now - startTime).getUTCHours();
  o.slaTimeMinutes = o.slaTimeHours * 60 + new Date(now - startTime).getUTCMinutes();
  o.slaTimeSeconds = new Date(now - startTime).getUTCSeconds();

  o.time = `${o.slaTimeMinutes.toLocaleString('en-US', {
    minimumIntegerDigits: 2,
    useGrouping: false,
    timezone,
  })}:${o.slaTimeSeconds.toLocaleString('en-US', {
    minimumIntegerDigits: 2,
    useGrouping: false,
    timezone,
  })}`;
  o.slaTime = Math.ceil((Date.now() - o.kitchen_started_at) / 1000);
  o.status = o.slaTime > o.out_of_sla_in * 60 ? 'out-of-sla' : 'in-sla';
  // if order is out of sla show notification
  if (o.status === 'out-of-sla' && !cachedSlaOrders.value.includes(o.id)) {
    cachedSlaOrders.value.push(o.id);
    if (selectedCategoryOrders.value.length > 0) {
      notifyAlarm(selectedCategoryOrders.value.length);
    }
  }
};
const isNewOrdersDetected = newOrders => {
  if (cachedOrders.value.length === 0) {
    cachedOrders.value = newOrders;
    return true;
  }

  const newOrderIds = newOrders.map(order => order.id);
  const cachedOrderIds = cachedOrders.value.map(order => order.id);

  const isNewOrder = newOrderIds.some(id => !cachedOrderIds.includes(id));

  if (isNewOrder) {
    cachedOrders.value = newOrders; // Update cached orders
  }

  return isNewOrder;
};

const removeAuthentication = () => {
  emit('removeAuthentication');
};
let slaInterval = null;
let categoryDetailsInterval = null;
const SLA_REFETCHING = 5 * 60 * 1000; // 5 minutes
// --- Lifecycle Hooks ---
onMounted(() => {
  // Global click listener for sidebar
  document.addEventListener('click', () => {
    displaySidebarMenu.value = false;
  });

  configureAxiosInstance();
  getCategoryDetails();

  // clear the cached notifications after 5 minutes
  slaInterval = setInterval(() => {
    cachedSlaOrders.value = [];
  }, SLA_REFETCHING);
});

const subscribeForPooling = () => {
  categoryDetailsInterval = setInterval(() => {
    if (categoryIndex.value) {
      console.log('Fetching category orders...');
      fetchCategoryOrders();
    }
  }, 5 * 60 * 1000); // Fetch every 5 minutes
};

const unsubscribeFromPooling = () => {
  if (categoryDetailsInterval) clearInterval(categoryDetailsInterval);
};

onBeforeUnmount(() => {
  // Clear all intervals to prevent memory leaks
  intervals.value.forEach(interval => clearInterval(interval));
  intervals.value = [];
  document.removeEventListener('click', () => {
    displaySidebarMenu.value = false;
  });
  if (slaInterval) clearInterval(slaInterval);

  unsubscribeFromPooling();
});

const fetchCategoryOrders = () => {
  getCategoryOrders();
  getCategoryUpcomingOrders();
  getCategoryAggregatedOrders();
};
</script>

<style>
body {
  background-color: #e5e5e5;
}

button:active {
  outline: none;
}

.mark {
  margin: auto;
}
.aggregateGrid {
  grid-template-rows: repeat(3, minmax(0, 1fr));
  grid-template-columns: repeat(2, minmax(0, 1fr)) 1fr;
  gap: 1rem;
  padding: 2rem;
  height: 90vh;
}
.order-details-wrraper {
  grid-row: 1 / span 3;
  overflow: scroll;
}
.aggregated-wrraper {
  border-left: 2px solid gray;
  grid-row: 1 / span 3;
}
</style>
